export function getStackDisplayName(stack, maxLength) {
  if (!stack) return '';
  const id = typeof stack.id === 'number' ? stack.id : Number(stack.id);
  const rawName = (stack.name ?? '').toString().trim();
  const base = rawName.length > 0 ? rawName : (Number.isFinite(id) ? `#${id}` : '#');
  if (typeof maxLength === 'number' && maxLength > 0) {
    if (base.length <= maxLength) return base;
    return base.substring(0, maxLength) + '...';
  }
  return base;
}

/**
 * Get display name for bot stacks with custom formatting
 * @param {object} stack - The stack object
 * @param {object} currentUser - The current user object
 * @returns {string} Formatted display name for bot stacks
 */
export function getBotStackDisplayName(stack, currentUser) {
  if (!stack || !stack.bot) return getStackDisplayName(stack);
  
  const botName = stack.bot.name || 'Bot';
  const currentUserName = currentUser?.username || 'You';
  
  // Check if this is a USER_DRIVEN bot with executor information
  const isUserDriven = stack.bot.type === 'USER_DRIVEN';
  const hasExecutor = isUserDriven && stack.bot.executor;
  
  if (stack.bot.audience) {
    if (stack.bot.audience.user) {
      const baseText = `${botName} stack for ${currentUserName} & ${stack.bot.audience.user.username}`;
      return hasExecutor ? `${baseText}\nGenerated by ${stack.bot.executor.username}` : baseText;
    } else if (stack.bot.audience.group) {
      const baseText = `${botName} group stack with ${stack.bot.audience.group.name}`;
      return hasExecutor ? `${baseText}\nGenerated by ${stack.bot.executor.username}` : baseText;
    }
  }
  
  // Fallback if no audience info
  const baseText = `${botName} stack`;
  return hasExecutor ? `${baseText}\nGenerated by ${stack.bot.executor.username}` : baseText;
}

/**
 * Calculate dynamic font size based on actual text width to prevent overflow
 * @param {string} text - The text to measure
 * @param {number} maxWidth - Maximum available width in pixels (default: 280px for typical mobile card)
 * @param {number} baseSize - Base font size (default: 16)
 * @param {number} minSize - Minimum font size (default: 10)
 * @returns {number} Calculated font size in pixels
 */
export const getDynamicFontSize = (text, maxWidth = 280, baseSize = 16, minSize = 10) => {
  if (!text || text.length === 0) {
    return baseSize;
  }

  // Create a temporary canvas to measure text width accurately
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  
  // Try with base size first
  context.font = `${baseSize}px -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif`;
  const textWidth = context.getTextMetrics ? context.getTextMetrics(text).width : context.measureText(text).width;
  
  // If text fits at base size, no need to reduce
  if (textWidth <= maxWidth) {
    return baseSize;
  }
  
  // Binary search for the optimal font size
  let low = minSize;
  let high = baseSize;
  let optimalSize = minSize;
  
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    context.font = `${mid}px -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif`;
    const width = context.getTextMetrics ? context.getTextMetrics(text).width : context.measureText(text).width;
    
    if (width <= maxWidth) {
      optimalSize = mid;
      low = mid + 1; // Try a larger size
    } else {
      high = mid - 1; // Try a smaller size
    }
  }
  
  return optimalSize;
};

/**
 * Get dynamic styles for stack position inputs based on text length
 * @param {string} text - The text content
 * @param {number} baseColor - The base text color
 * @param {string} backgroundColor - The background color
 * @returns {object} Style object with dynamic font size and consistent height
 */
export const getPositionInputStyles = (text, baseColor, backgroundColor) => {
  // Adjust parameters for 48-character limit: smaller base size, lower minimum
  const fontSize = getDynamicFontSize(text, 280, 14, 8);
  
  return {
    width: '100%',
    padding: '10px',
    border: '2px solid transparent',
    borderRadius: '6px',
    fontWeight: '700',
    fontSize: `${fontSize}px`,
    color: baseColor,
    backgroundColor: backgroundColor,
    minHeight: '44px', // Consistent height for all input cards
    display: 'flex',
    alignItems: 'center',
  };
};

/**
 * Get dynamic styles for displaying position labels in stack views
 * @param {string} text - The text content
 * @param {string} backgroundColor - The background color
 * @param {string} textColor - The text color
 * @returns {object} Style object with dynamic font size and consistent height
 */
export const getPositionDisplayStyles = (text, backgroundColor, textColor) => {
  const fontSize = getDynamicFontSize(text, 260, 14, 10); // Smaller base size for display, account for padding
  
  return {
    padding: '8px',
    borderRadius: '6px',
    backgroundColor: backgroundColor,
    color: textColor,
    fontWeight: '700',
    fontSize: `${fontSize}px`,
    minHeight: '36px', // Consistent height for all display cards
    display: 'flex',
    alignItems: 'center',
  };
};


